import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EntitySpec, FieldSpec } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { ErrorBox, PageHeader, Spinner, Table } from "../components/ui.js";
import { Modal } from "../components/Modal.js";
import { FormEngine } from "../components/FormEngine.js";

const CADASTRO_ORDER = ["pbtc", "curso_autor", "tarifas", "regioes", "regioes_usiminas"];

export function Cadastros() {
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const [entity, setEntity] = useState("pbtc");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [creating, setCreating] = useState(false);

  const catalog = useQuery({
    queryKey: ["catalog"],
    queryFn: async () => (await api.get("/catalog")).data as {
      entity: string;
      label: string;
      group: string;
      showInCadastros: boolean;
    }[],
  });
  const spec = useQuery({ queryKey: ["spec", entity], queryFn: async () => (await api.get(`/catalog/${entity}`)).data as EntitySpec });
  const list = useQuery({
    queryKey: ["list", entity, q],
    queryFn: async () => (await api.get(`/entities/${entity}?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`)).data as { rows: Record<string, unknown>[]; total: number },
  });

  const save = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (editing) return (await api.patch(`/entities/${entity}/${editing[spec.data!.primaryKey]}`, data)).data;
      return (await api.post(`/entities/${entity}`, data)).data;
    },
    onSuccess: () => { setCreating(false); setEditing(null); qc.invalidateQueries({ queryKey: ["list", entity] }); },
  });
  const del = useMutation({
    mutationFn: async (id: unknown) => (await api.delete(`/entities/${entity}/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["list", entity] }),
  });

  const cadastros = (catalog.data ?? [])
    .filter((e) => e.group === "cadastro" && e.showInCadastros)
    .sort((a, b) => CADASTRO_ORDER.indexOf(a.entity) - CADASTRO_ORDER.indexOf(b.entity));
  const writable = canWrite(entity);
  // A grade permanece compacta; o formulário contém todos os campos editáveis.
  const editable = spec.data?.fields.filter((f) => !f.computed) ?? [];
  const cols: FieldSpec[] = editable.filter((f) => f.type !== "checklist").slice(0, 6);

  return (
    <div>
      <PageHeader
        title="Cadastros"
        subtitle="Veículos, motoristas, tarifas e regiões"
        action={writable && <button className="btn-primary" onClick={() => { setEditing(null); setCreating(true); }}>+ Novo</button>}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {cadastros.map((e) => (
          <button
            key={e.entity}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${entity === e.entity ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
            onClick={() => { setEntity(e.entity); setQ(""); }}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          className="input max-w-md"
          data-testid="cadastros-search"
          placeholder="Buscar registros…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {del.isError && <div className="mb-3"><ErrorBox error={del.error} /></div>}
      {list.isLoading || spec.isLoading ? <Spinner /> : (
        <Table
          columns={[
            ...cols.map((f) => ({ key: f.name, label: f.label })),
            {
              key: "_acoes", label: "", render: (_v: unknown, row: Record<string, unknown>) => (
                <div className="flex gap-2">
                  {writable && <button className="text-xs text-brand-600 hover:underline" onClick={() => { setCreating(false); setEditing(row); }}>editar</button>}
                  {writable && <button className="text-xs text-red-500 hover:underline" onClick={() => { if (confirm("Excluir?")) del.mutate(row[spec.data!.primaryKey]); }}>excluir</button>}
                </div>
              ),
            },
          ]}
          rows={list.data?.rows ?? []}
        />
      )}
      <p className="mt-3 text-xs text-slate-400">{list.data?.total ?? 0} registro(s).</p>

      {(creating || editing) && spec.data && (
        <Modal title={`${editing ? "Editar" : "Novo"} — ${spec.data.label}`} onClose={() => { setCreating(false); setEditing(null); }}>
          {save.isError && <div className="mb-3"><ErrorBox error={save.error} /></div>}
          <FormEngine
            spec={spec.data}
            initial={editing ?? {}}
            mode={editing ? "update" : "create"}
            onSubmit={(d) => save.mutate(d)}
            onCancel={() => { setCreating(false); setEditing(null); }}
            submitting={save.isPending}
          />
        </Modal>
      )}
    </div>
  );
}
